// src/components/reporting/ReportingDashboard.js - Enhanced with user activity drill-down navigation
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useRoles } from '../../hooks/useRoles';
import { useApiCall } from '../../hooks/useApiCall';
import apiService from '../../services/apiService';
import AdHocAnalysis from './AdHocAnalysis'; // NEW: Added for ad hoc analysis
import AnalyticsPage from './AnalyticsPage'; // NEW: Added for analytics reports
import UsageReport from './usage/UsageReport'; // Usage & Cost reporting
import ReportDetailPage from './ReportDetailPage'; // NEW: Added for direct report navigation
import ReviewConversations from './ReviewConversations';
import ReviewConversationDetail from './ReviewConversationDetail';
import RealAssessmentPage from './RealAssessmentPage';
import CoachCohortReports from './cohort/CoachCohortReports'; // NEW: Coach cohort-scoped reporting
import CohortThemesReports from './cohort/CohortThemesReports'; // Cohort Themes (SuperAdmin/SCM)
import '../admin/Admin.css';
import './Reporting.css';

const ReportingDashboard = () => {
  const { user } = useAuth();
  const { userProfile } = useRoles();
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [availableTenants, setAvailableTenants] = useState([]);
  const [activeReport, setActiveReport] = useState(null);
  const [selectedReportId, setSelectedReportId] = useState(null); // NEW: Track selected analytics report
  const [selectedReviewConversationId, setSelectedReviewConversationId] = useState(null);
  const [selectedReviewConversationTitle, setSelectedReviewConversationTitle] = useState(null);
  const [selectedReviewUserId, setSelectedReviewUserId] = useState(null);
  const [selectedReviewUserData, setSelectedReviewUserData] = useState(null);
  const [breadcrumbs, setBreadcrumbs] = useState([]);
  
  const { loading, error, makeRequest } = useApiCall();

  // Check user access level - simplified from your original logic
  const hasReportingAccess = userProfile?.roles?.includes('Admin') ||
                           userProfile?.roles?.includes('SuperAdmin') ||
                           userProfile?.roles?.includes('SuperContentManager') ||
                           userProfile?.roles?.includes('Coach');

  // Only SuperAdmin can select tenants and view system performance
  const isSuperAdmin = userProfile?.roles?.includes('SuperAdmin');
  const canSelectTenant = isSuperAdmin;
  const isSuperContentManager = userProfile?.roles?.includes('SuperContentManager');
  const isAdmin = userProfile?.roles?.includes('Admin');
  const isCoach = userProfile?.roles?.includes('Coach');
  const isOnlyAdmin = isAdmin && !isSuperAdmin && !isSuperContentManager && !isCoach;
  // RPT-001/002/003's own role gate — SuperContentManager excluded, unlike the
  // page-level hasReportingAccess gate above (which still covers Analytics/Ad
  // Hoc/Usage etc., out of scope for this fix — see rbac-matrix-template.md).
  const canSeeActivityReports = isSuperAdmin || isSuperContentManager || isAdmin || isCoach;
  const canSeeCohortReports = isSuperAdmin || isCoach;
  const canSeeAdHocReports = isSuperAdmin;
  const canSeeAnalyticReports = isSuperAdmin;
  // Cohort Themes carries its own tenant picker (SCM can't use the dashboard's),
  // so its tile doesn't wait on selectedTenant.
  const canSeeCohortThemes = isSuperAdmin || isSuperContentManager;

  useEffect(() => {
    if (userProfile && hasReportingAccess) {
      loadAvailableTenants();
    }
  }, [userProfile]);

  // Access control check - early return if no access
  if (!hasReportingAccess) {
    return (
      <div className="reporting-dashboard">
        <div className="card text-center py-12">
          <div className="icon-container gray">
            <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 002 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-700 mb-2">
            Access Restricted
          </h3>
          <p className="text-gray-500 mb-2">
            You do not have permission to access reporting features.
          </p>
          <p className="text-sm text-gray-400">
            Contact your administrator for access.
          </p>
        </div>
      </div>
    );
  }

  const loadAvailableTenants = async () => {
    try {
      if (canSelectTenant) {
        // SuperAdmin: Load all tenants
        const data = await makeRequest(
          () => apiService.get('/api/v1/tenants', user),
          'Loading tenants...'
        );
        
        if (data.success) {
          const tenantList = data.data?.tenants || data.data || [];
          setAvailableTenants(tenantList);
          
          // IMPROVED: Default to user's own tenant first, then fallback to first available
          selectDefaultTenant(tenantList);
        }
      } else {
        // Regular Admin: Use their tenant without querying the tenants API
        if (userProfile?.tenant) {
          const tenantList = [userProfile.tenant];
          setAvailableTenants(tenantList);
          setSelectedTenant(userProfile.tenant);
        }
      }
    } catch (err) {
      console.error('Error loading tenants:', err);
    }
  };

  // NEW: Smart tenant selection logic (from your original implementation)
  const selectDefaultTenant = (tenantList) => {
    if (!tenantList || tenantList.length === 0) {
      setSelectedTenant(null);
      return;
    }

    // If user already has a selected tenant and it's still available, keep it
    if (selectedTenant && tenantList.find(t => t.id === selectedTenant.id)) {
      return;
    }

    // Priority 1: User's own tenant (if they belong to one)
    if (userProfile?.tenant) {
      const userTenant = tenantList.find(tenant => tenant.id === userProfile.tenant.id);
      if (userTenant) {
        // console.log('Defaulting to user\'s own tenant:', userTenant.name);
        setSelectedTenant(userTenant);
        return;
      }
    }

    // Priority 2: Look for tenant by matching user's tenant info in other ways
    if (userProfile?.tenantId) {
      const userTenant = tenantList.find(tenant => tenant.id === userProfile.tenantId);
      if (userTenant) {
        // console.log('Defaulting to user\'s tenant by ID:', userTenant.name);
        setSelectedTenant(userTenant);
        return;
      }
    }

    // Priority 3: If user has email domain, try to match tenant by domain/slug
    if (userProfile?.email) {
      const emailDomain = userProfile.email.split('@')[1];
      const domainTenant = tenantList.find(tenant => 
        tenant.slug?.includes(emailDomain.split('.')[0]) ||
        tenant.domain === emailDomain
      );
      if (domainTenant) {
        // console.log('Defaulting to tenant by email domain match:', domainTenant.name);
        setSelectedTenant(domainTenant);
        return;
      }
    }

    // Fallback: First tenant in the list
    // console.log('Defaulting to first available tenant:', tenantList[0].name);
    setSelectedTenant(tenantList[0]);
  };

  const handleTenantSelect = (tenant) => {
    setSelectedTenant(tenant);
    // Reset active report when changing tenants
    setActiveReport(null);
    setSelectedReviewConversationId(null);
    setSelectedReviewConversationTitle(null);
    setBreadcrumbs([]);
  };

  const handleViewAdHocAnalysis = () => {
    setActiveReport('ad-hoc-analysis');
    setBreadcrumbs([
      {
        label: 'Reporting Dashboard',
        onClick: handleBackToReporting,
        active: false
      },
      {
        label: 'Ad Hoc Analysis',
        active: true
      }
    ]);
  };

  const handleViewAnalytics = () => {
    setActiveReport('analytics');
    setBreadcrumbs([
      {
        label: 'Reporting Dashboard',
        onClick: handleBackToReporting,
        active: false
      },
      {
        label: 'Analytics Reports',
        active: true
      }
    ]);
  };

  const handleViewCoachCohortReports = () => {
    setActiveReport('coach-cohort-reports');
    setBreadcrumbs([
      {
        label: 'Reporting Dashboard',
        onClick: handleBackToReporting,
        active: false
      },
      {
        label: 'Cohort Reporting',
        active: true
      }
    ]);
  };

  const handleViewCohortThemes = () => {
    setActiveReport('cohort-themes');
    setBreadcrumbs([
      { label: 'Reporting Dashboard', onClick: handleBackToReporting, active: false },
      { label: 'Cohort Themes', active: true }
    ]);
  };

  const handleViewUsageReport = () => {
    setActiveReport('usage-report');
    setBreadcrumbs([
      {
        label: 'Reporting Dashboard',
        onClick: handleBackToReporting,
        active: false
      },
      {
        label: 'Usage & Costs',
        active: true
      }
    ]);
  };

  // NEW: Handle navigating directly to a specific report (used by both AdHocAnalysis and AnalyticsPage after creation)
  const handleViewReport = (reportId) => {
    setSelectedReportId(reportId);
    setActiveReport('report-detail');
    setBreadcrumbs([
      {
        label: 'Reporting Dashboard',
        onClick: handleBackToReporting,
        active: false
      },
      {
        label: 'Report Detail',
        active: true
      }
    ]);
  };

  const handleBackToReporting = () => {
    setActiveReport(null);
    setSelectedReportId(null); // NEW: Reset report selection
    setSelectedReviewConversationId(null);
    setSelectedReviewConversationTitle(null);
    setBreadcrumbs([]);
  };

  const handleViewRealAssessment = () => {
    setActiveReport('real-assessment');
    setBreadcrumbs([
      { label: 'Reporting Dashboard', onClick: handleBackToReporting, active: false },
      { label: 'REAL Assessment', active: true }
    ]);
  };

  const handleViewReviewConversations = () => {
    setActiveReport('review-conversations');
    setBreadcrumbs([
      { label: 'Reporting Dashboard', onClick: handleBackToReporting, active: false },
      { label: 'Review Conversations', active: true }
    ]);
  };

  const handleViewReviewConversationDetail = (conversationId, conversationTitle, userId, userData) => {
    setSelectedReviewConversationId(conversationId);
    setSelectedReviewConversationTitle(conversationTitle);
    setSelectedReviewUserId(userId || null);
    setSelectedReviewUserData(userData || null);
    setActiveReport('review-conversation-detail');
    setBreadcrumbs([
      { label: 'Reporting Dashboard', onClick: handleBackToReporting, active: false },
      { label: 'Review Conversations', onClick: handleBackToReviewConversations, active: false },
      { label: conversationTitle || 'Conversation Detail', active: true }
    ]);
  };

  const handleBackToReviewConversations = () => {
    setSelectedReviewConversationId(null);
    setSelectedReviewConversationTitle(null);
    setActiveReport('review-conversations');
    setBreadcrumbs([
      { label: 'Reporting Dashboard', onClick: handleBackToReporting, active: false },
      { label: 'Review Conversations', active: true }
    ]);
  };

  // Access level information - improved from your original
  const getAccessLevelInfo = () => {
    if (userProfile?.roles?.includes('SuperAdmin')) {
      return {
        level: 'Super Admin Access',
        description: 'You have full access to all tenant reports and system analytics.',
        badge: 'role-badge super-admin'
      };
    }
    if (userProfile?.roles?.includes('Admin')) {
      return {
        level: 'Admin Access',
        description: 'You can view reports for all tenants in the system.',
        badge: 'role-badge admin'
      };
    }
    if (userProfile?.roles?.includes('SuperContentManager')) {
      return {
        level: 'Content Manager Access',
        description: `You can view reports for ${userProfile?.tenant?.name || 'your organization'}.`,
        badge: 'role-badge content-manager'
      };
    }
    if (userProfile?.roles?.includes('Coach')) {
      return {
        level: 'Coach Access',
        description: 'You can view reports for the users in your assigned cohorts.',
        badge: 'role-badge limited'
      };
    }
    return {
      level: 'Limited Access',
      description: `You can view reports for ${userProfile?.tenant?.name || 'your organization'}.`,
      badge: 'role-badge limited'
    };
  };

  const accessLevelInfo = getAccessLevelInfo();

  if (activeReport === 'real-assessment' && selectedTenant) {
    return (
      <RealAssessmentPage
        tenant={selectedTenant}
        onBack={handleBackToReporting}
      />
    );
  }

  if (activeReport === 'review-conversation-detail' && selectedReviewConversationId) {
    return (
      <ReviewConversationDetail
        conversationId={selectedReviewConversationId}
        onBack={handleBackToReviewConversations}
        breadcrumbs={breadcrumbs}
      />
    );
  }

  if (activeReport === 'review-conversations' && selectedTenant) {
    return (
      <ReviewConversations
        tenant={selectedTenant}
        onNavigateBack={handleBackToReporting}
        onViewConversationDetail={handleViewReviewConversationDetail}
        breadcrumbs={breadcrumbs}
        initialSelectedUserId={selectedReviewUserId}
        initialSelectedUserData={selectedReviewUserData}
      />
    );
  }

  // NEW: Render a specific report directly (after creation from AdHocAnalysis or AnalyticsPage)
  if (activeReport === 'report-detail' && selectedReportId) {
    return (
      <ReportDetailPage
        reportId={selectedReportId}
        onBack={handleBackToReporting}
      />
    );
  }

  // NEW: Render coach cohort reports
  if (activeReport === 'coach-cohort-reports' && selectedTenant) {
    return (
      <CoachCohortReports
        tenant={selectedTenant}
        breadcrumbs={breadcrumbs}
      />
    );
  }

  if (activeReport === 'cohort-themes' && canSeeCohortThemes) {
    return (
      <CohortThemesReports
        defaultTenant={selectedTenant}
        breadcrumbs={breadcrumbs}
      />
    );
  }

  // NEW: Render ad hoc analysis
  if (activeReport === 'ad-hoc-analysis' && selectedTenant) {
    return (
      <AdHocAnalysis 
        tenant={selectedTenant}
        onBack={handleBackToReporting}
        onViewReport={handleViewReport}
      />
    );
  }

  // NEW: Render analytics page
  if (activeReport === 'analytics' && selectedTenant) {
    return (
      <AnalyticsPage 
        tenant={selectedTenant}
        onBack={handleBackToReporting}
        onViewReport={handleViewReport}
      />
    );
  }

    // Render Usage & Costs report
  if (activeReport === 'usage-report') {
    return (
      <UsageReport
        onBack={handleBackToReporting}
      />
    );
  }
  // Render main reporting dashboard
  return (
    <div className="admin-page reporting-dashboard">
      {/* Enhanced Header with Tenant Selection */}
      <div className="reporting-header mb-6">
        <div className="flex flex-between">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <h2 className="text-2xl font-semibold text-gray-800">Reporting Dashboard</h2>
              <div className={`role-badge ${accessLevelInfo.badge}`}>
                {accessLevelInfo.level}
              </div>
            </div>
            <p className="text-gray-600">
              {accessLevelInfo.description}
              {selectedTenant && (
                <span className="block mt-1">
                  Currently viewing: <strong>{selectedTenant.name}</strong>
                </span>
              )}
            </p>
          </div>
          
          <div className="flex gap-2 items-end">
            {/* Tenant Selection UI — matches the select-based picker on the User Activity Report */}
            {canSelectTenant && (
              <div className="tenant-filter-group">
                <label className="tenant-filter-label" htmlFor="reporting-tenant-select">Tenant</label>
                <select
                  id="reporting-tenant-select"
                  className="tenant-filter-select"
                  value={selectedTenant?.id || ''}
                  onChange={(e) => {
                    const tenant = availableTenants.find((t) => String(t.id) === e.target.value);
                    if (tenant) handleTenantSelect(tenant);
                  }}
                  disabled={loading || availableTenants.length === 0}
                >
                  <option value="" disabled>Select a tenant…</option>
                  {availableTenants.map((t) => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </select>
              </div>
            )}

            <button
              onClick={loadAvailableTenants}
              className="btn btn-secondary"
              disabled={loading}
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              {loading ? 'Refreshing...' : 'Refresh'}
            </button>
          </div>
        </div>
      </div>

      {/* Error State */}
      {error && (
        <div className="alert alert-error mb-6">
          <strong>Error:</strong> {error}
          <button onClick={loadAvailableTenants} className="btn btn-secondary btn-sm ml-3">
            Try Again
          </button>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="text-center py-12">
          <div className="loading-dots mx-auto mb-3">
            <div className="loading-dot"></div>
            <div className="loading-dot"></div>
            <div className="loading-dot"></div>
          </div>
          <p className="text-gray-600">Loading tenant data...</p>
        </div>
      )}

      {/* Available Reports Overview */}
      <div className="reports-overview mb-8">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Available Reports</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          
          {/* User Activity Report Card — RPT-001/002/003, routed (not internal state) */}
          {canSeeActivityReports && (
            <Link to="/app/reports/activity" className="report-card card cursor-pointer hover:bg-blue-50 transition-colors">
              <div className="report-icon-container">
                <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <h4 className="text-lg font-semibold text-gray-800 mb-2">
                User Activity Reports
              </h4>
              <p className="text-sm text-gray-600 mb-4">
                {isOnlyAdmin
                  ? 'Review activity at the tenant or user level'
                  : "Drill from a user list into their conversations and each conversation's debrief"}
              </p>
            </Link>
          )}

          {/* Cohort Reporting - Coach only */}
          {canSeeCohortReports && (
            <div
              className={`report-card card ${selectedTenant ? 'cursor-pointer hover:bg-teal-50 transition-colors' : 'opacity-50 cursor-not-allowed'}`}
              onClick={selectedTenant && !loading ? handleViewCoachCohortReports : undefined}
            >
              <div className="report-icon-container">
                <svg className="w-8 h-8 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a4 4 0 00-3-3.87M9 20H4v-2a4 4 0 013-3.87m6-3.13a4 4 0 10-4-4 4 4 0 004 4zm6 1a4 4 0 10-4-4" />
                </svg>
              </div>
              <h4 className="text-lg font-semibold text-gray-800 mb-2">
                Cohort Reporting
              </h4>
              <p className="text-sm text-gray-600 mb-4">
                Browse the users in your assigned cohorts and review their case study assessments
              </p>
            </div>
          )}

          {/* Analytic Reports */}
          {canSeeAnalyticReports && (
          <div
            className={`report-card card ${selectedTenant ? 'cursor-pointer hover:bg-purple-50 transition-colors' : 'opacity-50 cursor-not-allowed'}`}
            onClick={selectedTenant && !loading ? handleViewAnalytics : undefined}
          >
            <div className="report-icon-container">
              <svg className="w-8 h-8 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z" />
              </svg>
            </div>
            <h4 className="text-lg font-semibold text-gray-800 mb-2">
              Analytic Reports
            </h4>
            <p className="text-sm text-gray-600 mb-4">
              View, manage, and run analytic reports 
            </p>
          </div>
          )}

          {/* Cohort Themes - SuperAdmin and SuperContentManager */}
          {canSeeCohortThemes && (
            <div
              className={`report-card card ${!loading ? 'cursor-pointer hover:bg-blue-50 transition-colors' : 'opacity-50 cursor-not-allowed'}`}
              onClick={!loading ? handleViewCohortThemes : undefined}
            >
              <div className="report-icon-container">
                <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8h2a2 2 0 012 2v6a2 2 0 01-2 2h-2v4l-4-4H9a1.994 1.994 0 01-1.414-.586m0 0L11 14h4a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2v4l.586-.586z" />
                </svg>
              </div>
              <h4 className="text-lg font-semibold text-gray-800 mb-2">
                Cohort Themes
              </h4>
              <p className="text-sm text-gray-600 mb-4">
                Who a cohort&apos;s participants are having high-stakes conversations with, and what they&apos;re about
              </p>
              <div className="report-meta text-xs text-gray-500">
                SuperAdmin and Content Manager
              </div>
            </div>
          )}

          {/* Ad Hoc Analysis - SuperAdmin, SuperContentManager, and Coach */}
          {canSeeAdHocReports && (
            <div
              className={`report-card card ${selectedTenant ? 'cursor-pointer hover:bg-blue-50 transition-colors' : 'opacity-50 cursor-not-allowed'}`}
              onClick={selectedTenant && !loading ? handleViewAdHocAnalysis : undefined}
            >
              <div className="report-icon-container">
                <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <h4 className="text-lg font-semibold text-gray-800 mb-2">
                Ad Hoc Analysis
              </h4>
              <p className="text-sm text-gray-600 mb-4">
                Use open-ended questions to explore trends or patterns in the conversations
              </p>
            </div>
          )}

         {/* System Usage and Performance - SuperAdmin only */}
          {isSuperAdmin && (
            <div
              className="report-card card cursor-pointer hover:bg-green-50 transition-colors"
              onClick={!loading ? handleViewUsageReport : undefined}
            >
              <div className="report-icon-container">
                <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 8v8m-4-5v5m-4-2v2m-2 4h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
              <h4 className="text-lg font-semibold text-gray-800 mb-2">
                Usage and Costs
              </h4>
              <p className="text-sm text-gray-600 mb-4">
                Service usage metrics and associated costs across all tenants
              </p>
              <div className="report-meta text-xs text-gray-500">
                SuperAdmin only
              </div>
            </div>
          )}

          {/* Review Conversations - SuperAdmin only */}
          {isSuperAdmin && (
            <div
              className={`report-card card ${selectedTenant ? 'cursor-pointer hover:bg-red-50 transition-colors' : 'opacity-50 cursor-not-allowed'}`}
              onClick={selectedTenant && !loading ? handleViewReviewConversations : undefined}
            >
              <div className="report-icon-container">
                <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                </svg>
              </div>
              <h4 className="text-lg font-semibold text-gray-800 mb-2">
                Review Conversations
              </h4>
              <p className="text-sm text-gray-600 mb-4">
                Browse users by conversation count, inspect AI interactions, and delete conversations
              </p>
              <div className="report-meta text-xs text-gray-500">
                SuperAdmin only
              </div>
            </div>
          )}

           {/*  Assessment Debriefs - SuperAdmin only */}
          {isSuperAdmin && (
            <div
              className={`report-card card ${selectedTenant ? 'cursor-pointer hover:bg-amber-50 transition-colors' : 'opacity-50 cursor-not-allowed'}`}
              onClick={selectedTenant && !loading ? handleViewRealAssessment : undefined}
            >
              <div className="report-icon-container">
                <svg className="w-8 h-8 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                </svg>
              </div>
              <h4 className="text-lg font-semibold text-gray-800 mb-2">
                Case Study Debriefs
              </h4>
              <p className="text-sm text-gray-600 mb-4">
                Review Case Study Debriefs, or generate one if Case Study didn&apos;t complete
              </p>
              <div className="report-meta text-xs text-gray-500">
                SuperAdmin only
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Loading/Error States for Reports */}
      <LoadingErrorStates loading={loading} error={error} onRetry={loadAvailableTenants} />

      {/* Empty State */}
      {!loading && !error && availableTenants.length === 0 && (
        <div className="card text-center py-12">
          <div className="icon-container gray">
            <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-700 mb-2">
            No Tenants Available
          </h3>
          <p className="text-gray-500">
            No tenants are available for reporting at this time.
          </p>
        </div>
      )}
    </div>
  );
};

// Loading and Error States Component
const LoadingErrorStates = ({ loading, error, onRetry }) => {
  if (!loading && !error) return null;

  return (
    <div className="text-center py-8">
      {loading && (
        <div className="loading-dots mx-auto mb-3">
          <div className="loading-dot"></div>
          <div className="loading-dot"></div>
          <div className="loading-dot"></div>
        </div>
      )}
      {error && (
        <div className="alert alert-error">
          <strong>Error:</strong> {error}
          <button onClick={onRetry} className="btn btn-secondary btn-sm ml-3">
            Try Again
          </button>
        </div>
      )}
    </div>
  );
};

// PropTypes
LoadingErrorStates.propTypes = {
  loading: PropTypes.bool.isRequired,
  error: PropTypes.string,
  onRetry: PropTypes.func.isRequired
};

export default ReportingDashboard;